import { Box, Paper } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import SlotAssetsDetails from '../edit-details/slot-assets-details';
import { FlowEndSlotAssetsDetails } from '../edit-details/flow-end-slot-assets-details';
import type { FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import type { FlowDetailMode } from '../flow-detail.types';
import {
  FlowSlotAssetsSlotListColumn,
  FlowSlotAssetsUnpublishedPlaceholder,
  resolveDefaultSlotAssetsListMode,
} from './flow-slot-assets-slot-list-column';
import { useFlowStore } from '@/state/stores/use-flow-store';
import type { FlowSlotAssetsWideAnchor } from './flow-slot-assets-wide-anchor';
import type { Mode } from '@/types/entity-mode';

interface FlowSlotAssetsTabProps {
  setShowFlowDetails: React.Dispatch<React.SetStateAction<FlowDetailMode | undefined>>;
  setFlowDetailsDirection: React.Dispatch<React.SetStateAction<'up' | 'down' | undefined>>;
  onSlotSelect?: (slot: FlowSlotDto, isUserInitiated?: boolean) => void;
  onShowTimingAlert?: () => void;
  onShowPublishAlert?: () => void;
  selectedSlot?: FlowSlotDto;
  slotTitle: string;
  onSlotDeleted: () => void;
  eventSlotIdParam?: string | null;
}

const scrollbarSx = {
  '&::-webkit-scrollbar': { width: '8px' },
  '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
  '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '4px' },
};

const sharedColumnSx = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
  height: '100%',
  px: 1,
  pt: 1,
  ...scrollbarSx,
};

export default function FlowSlotAssetsTab({
  setShowFlowDetails,
  setFlowDetailsDirection,
  onSlotSelect,
  onShowTimingAlert,
  onShowPublishAlert,
  selectedSlot,
  slotTitle,
}: FlowSlotAssetsTabProps) {
  const flowPlan = useFlowStore((s) => s.flowPlan);
  const flowOrganize = useFlowStore((s) => s.flowOrganize);
  const hasPlanMode = !!flowPlan;
  const hasOrganizeMode = !!flowOrganize;
  const [flowSlotAssetsWideAnchor, setFlowSlotAssetsWideAnchor] = useState<FlowSlotAssetsWideAnchor>('start');
  const [listViewMode, setListViewMode] = useState<Mode>(() =>
    resolveDefaultSlotAssetsListMode(hasPlanMode, hasOrganizeMode),
  );

  useEffect(() => {
    setListViewMode((current) => {
      if (current === 'PLAN' && !hasPlanMode && hasOrganizeMode) return 'ORGANIZE';
      if (current === 'ORGANIZE' && !hasOrganizeMode && hasPlanMode) return 'PLAN';
      return current;
    });
  }, [hasPlanMode, hasOrganizeMode]);

  useEffect(() => {
    if (selectedSlot) {
      setFlowSlotAssetsWideAnchor('start');
    }
  }, [selectedSlot]);

  const flowForAssets = useMemo(() => {
    if (listViewMode === 'ORGANIZE' && flowOrganize) return flowOrganize;
    return flowPlan ?? flowOrganize;
  }, [listViewMode, flowPlan, flowOrganize]);

  if (!flowForAssets) {
    return (
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, width: '100%', overflow: 'hidden' }}>
        <FlowSlotAssetsUnpublishedPlaceholder />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', gap: 1, flex: 1, minHeight: 0, width: '100%', overflow: 'hidden' }}>
      <FlowSlotAssetsSlotListColumn
        setShowFlowDetails={setShowFlowDetails}
        setFlowDetailsDirection={setFlowDetailsDirection}
        onSlotSelect={onSlotSelect}
        onShowTimingAlert={onShowTimingAlert}
        onShowPublishAlert={onShowPublishAlert}
        flowSlotAssetsWideAnchor={flowSlotAssetsWideAnchor}
        onFlowSlotAssetsWideAnchorChange={setFlowSlotAssetsWideAnchor}
        listViewMode={listViewMode}
        onListViewModeChange={setListViewMode}
      />
      <Box sx={sharedColumnSx}>
        <Paper elevation={0} sx={{ height: '100%', px: 0 }}>
          {!selectedSlot && flowSlotAssetsWideAnchor === 'end' ? (
            <FlowEndSlotAssetsDetails flow={flowForAssets} />
          ) : (
            <SlotAssetsDetails selectedSlot={selectedSlot} slotTitle={slotTitle} listViewMode={listViewMode} />
          )}
        </Paper>
      </Box>
    </Box>
  );
}
